import React, { useEffect, useRef, useState, Fragment, useCallback, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { RootState, useAppDispatch } from '../../../store';
import Button from '../../shared/TailwindComponents/Button';
import Input from '../../shared/TailwindComponents/Input';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import { PiClockLight, PiDatabaseLight, PiFileLight, PiPaperPlaneLight, PiDiamondsFourLight, PiTrashLight, PiFileTextLight, PiDotsThreeVerticalLight, PiPaperclipLight, PiTestTubeLight, PiShareNetworkLight } from 'react-icons/pi';
import { hideModal, showModal } from '../../../store/actions/modalActions';
import Badge from '../../shared/TailwindComponents/Badge';
import { isBetaEnv } from '../../../utils/authUtils';
import { addDataToDataSourceSSE, chatWithDataSource, deleteDataSource, deleteFileFromDataSource, downloadDataSourceFile, getDataSourceInfo, listFilesInDataSource, revokeSharedAccess, updateDataSource, updateDataSourceSharedAccess } from '../../../store/actions/AIStudioAction';
import { showSidePanel } from '../../../store/actions/sidePanelActions';
import { FiLock } from 'react-icons/fi';
import { getMultiHubSocketService } from '../../../utils/MultiHubSocketService';
import { useSelector } from 'react-redux';
import DataSourceDetailsPanel, { ShareUser, AccessLevel } from './DataSourceDetailsPanel';
import DataSourceFiles from './DataSourceFilesPanel';
import { Menu, Transition } from '@headlessui/react';
import DetailFrame from '../../shared/Frames/DetailFrame';
import ProjectBadges from '../../Projects/ProjectBadges';
import PendingAccessRequests from '../../Projects/PendingAccessRequests';
import FilterBarWithTabs from '../../shared/TailwindComponents/FilterBarWithTabs';
import CollaboratorBadge, { getInitials } from '../../shared/TailwindComponents/CollaboratorsBadge';
import clsx from 'clsx';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, dataSourceDetailTourSteps, dataSourceDetailsPanelTourSteps, dataSourceFilesPanelTourSteps, dataSourceTestTourSteps } from '../../shared/FeatureTour';

const AIStudioIndexDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { dataSourceUuid } = useParams<{ dataSourceUuid: string }>();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const [activeTab, setActiveTab] = useState('details');
  const [indexInfo, setIndexInfo] = useState<any>(null);
  const [filesInIndex, setFilesInIndex] = useState<{ id?: string; name: string; url?: string }[]>([]);
  const [filesToAdd, setFilesToAdd] = useState<File[]>([]);
  const [isDeleting, setIdDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingFiles, setDeletingFiles] = useState<string[]>([]);
  const [isFilesLoading, setIsFilesLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState<{ role: 'user' | 'dataSource'; content: string }[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const { subscriptionActive } = useSelector((state: RootState) => state.subscription);
  const { complete, connected, steps, isConnecting, ok, summary, startedAt } = useSelector((state: RootState) => state.aistudio)
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const [isFetchingFiles, setIsFetchingFiles] = useState(false)
  const [shared, setShared] = useState<ShareUser[]>([]);
  const [isReindexing, setIsReindexing] = useState(false);

  const { isRunning: isDetailTourRunning, handleJoyrideCallback: handleDetailTourCallback } = useFeatureTour({
    tourId: 'data-source-detail',
    steps: dataSourceDetailTourSteps,
    readyToShow: !isLoading && !!indexInfo,
    autoStartDelay: 600,
  });

  const { isRunning: isDetailsPanelTourRunning, handleJoyrideCallback: handleDetailsPanelTourCallback } = useFeatureTour({
    tourId: 'data-source-details-panel',
    steps: dataSourceDetailsPanelTourSteps,
    readyToShow: !isLoading && !!indexInfo && activeTab === 'details',
    autoStartDelay: 1200,
  });

  const { isRunning: isFilesPanelTourRunning, handleJoyrideCallback: handleFilesPanelTourCallback } = useFeatureTour({
    tourId: 'data-source-files-panel',
    steps: dataSourceFilesPanelTourSteps,
    readyToShow: !isLoading && activeTab === 'files' && !isFetchingFiles,
    autoStartDelay: 800,
  });

  const { isRunning: isTestTourRunning, handleJoyrideCallback: handleTestTourCallback } = useFeatureTour({
    tourId: 'data-source-test',
    steps: dataSourceTestTourSteps,
    readyToShow: !isLoading && activeTab === 'test',
    autoStartDelay: 800,
  });



  const tabs = [
    { label: t('Details'), value: 'details', icon: <PiFileTextLight className='w-4 h-4' /> },
    { label: t('Files'), value: 'files', icon: <PiPaperclipLight className='w-4 h-4' /> },
  ];

  const rightTabs = [
  { label: t('Test'), value: 'test', icon: <PiTestTubeLight className='w-4 h-4' /> },
  ];
  const fetchIndexDetails = useCallback(async () => {
    const info = await dispatch(getDataSourceInfo(dataSourceUuid) as any);
    if (info?.status === 'success') {
      setIndexInfo(info.data);
    } else if (info?.status === 'error') {
      dispatch(showFeedbackModal({ showModal: true, message: info.message, modalType: 'error', duration: 3000 }));
    }
  }, [dataSourceUuid, dispatch]);


  const fetchIndexFiles = useCallback(async () => {
    setIsFetchingFiles(true)
    const data = await dispatch(listFilesInDataSource(dataSourceUuid) as any);
    if (data?.status === 'success') {
      setFilesInIndex(data.data.files);
    } else if (data?.status === 'error') {
      dispatch(showFeedbackModal({ showModal: true, message: data.message, modalType: 'error', duration: 3000 }));
    }
    setIsFetchingFiles(false)
  }, [dataSourceUuid, dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await fetchIndexDetails()
      setIsLoading(false);
    };
    fetchData();
  }, [dataSourceUuid, fetchIndexDetails]);

  useEffect(() => {
    if(activeTab === "files"){
      fetchIndexFiles()
    }
  }, [activeTab, fetchIndexFiles])



  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  // Sync shared_with from indexInfo
  useEffect(() => {
    setShared(indexInfo?.shared_with || []);
  }, [indexInfo?.shared_with]);

  const handleManageAccess = () => {
    dispatch(
      showSidePanel({
        type: 'manageResourceCollaborators',
        showSidePanel: true,
        width: '50%',
        message: t('Manage Access'),
        resourceUuid: dataSourceUuid,
        resourceType: 'datasource',
        // Normalize the data-source collaborator shape to what the side panel
        // expects: it reads `profile_picture` and `collaborator_uuid`, while
        // the data-source endpoint emits `avatarUrl` and `id`/`user_uuid`.
        collaborators: (shared || []).map((u: any) => ({
          ...u,
          collaborator_uuid: u.collaborator_uuid || u.user_uuid || u.id,
          user_id: u.user_id || u.id,
          profile_picture: u.profile_picture || u.avatarUrl || u.avatar_url || null,
          access: u.access || 'view',
          source: u.source || 'direct',
        })),
        currentUserUuid: userInfo?.UserUID,
        canAdmin: true,
        addCollaborator: async (uuid: string, userUuid: string, access: string) => {
          await dispatch(updateDataSourceSharedAccess(uuid, [...(shared as any[]), { id: userUuid, email: '', name: '', initials: '', avatarUrl: '', role: '', access: access as any }]) as any);
        },
        removeCollaborator: async (uuid: string, collabUuid: string) => {
          await dispatch(revokeSharedAccess(uuid, collabUuid) as any);
        },
        updateAccess: async (uuid: string, collabUuid: string, access: string) => {
          const updated = (shared as any[]).map((s: any) => s.id === collabUuid ? { ...s, access } : s);
          await dispatch(updateDataSourceSharedAccess(uuid, updated) as any);
        },
        onUpdate: async () => {
          await fetchIndexDetails();
        },
      })
    );
  };

  // Collaborators for header display
  const collaborators = useMemo(() => {
    return (shared || []).map((u) => ({
      name: u.name,
      initials: u.initials || getInitials(u.name),
      email: u.email,
      avatarURL: u.avatarUrl,
      access: u.access || 'view',
    }));
  }, [shared]);




  const handleAddData = async (files: File[]) => {
    if (!dataSourceUuid || files.length === 0) return;
    setIsFilesLoading(true);
    try {
      const resp = await dispatch(addDataToDataSourceSSE(dataSourceUuid, files) as any);
  
      if (resp?.status === "success") {
        setFilesToAdd([]);
        await fetchIndexFiles();
        await fetchIndexDetails();
      }
    } catch (err) {
      console.error("AddData SSE failed:", err);
    } finally {
      setIsFilesLoading(false);
    }
  };

  const handleDeleteIndex = async () => {
    dispatch(showModal({
      showModal: true,
      type: "confirmation",
      isDest: true,
      icon:  <PiTrashLight className='w-6 h-6 text-red-600' />,
      message: t("Delete Data Source"),
      subMsg: t("This data source and all associated files will be permanently deleted. This action cannot be undone."),
      onConfirm: async () => {
        setIdDeleting(true)
        const resp = await dispatch(deleteDataSource(dataSourceUuid) as any);
        setIdDeleting(false)
        if (resp?.status === 'success') {
          history.push('/data-sources');
        }
        dispatch(hideModal());
      },
      onCancel: () => {},
      duration: 3000,
      inputData: "Yes, Delete"
    })
    )

  };

  const handleChatSend = async () => {
    if (!chatInput.trim()) return;
    const userMessage = { role: 'user', content: chatInput };
    setChatHistory((prev: any) => [...prev, userMessage]);
    setChatInput('');
    setIsChatLoading(true);

    // Convert history format for API (dataSource -> assistant)
    const apiHistory = chatHistory.map((msg: any) => ({
      role: msg.role === 'dataSource' ? 'assistant' : msg.role,
      content: msg.content
    }));

    const response = await dispatch(chatWithDataSource(dataSourceUuid, chatInput, apiHistory) as any);
    setIsChatLoading(false);

    if (response) {
      const dataSourceMessage = { role: 'dataSource', content: response };
      setChatHistory((prev: any) => [...prev, dataSourceMessage]);
    } else {
      dispatch(showFeedbackModal({ showModal: true, message: t('Failed to get a response from the data source.'), modalType: 'error', duration: 3000 }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleChatSend();
    }
  };

  const handleDeleteFile = (fileId: string, fileName: string) => {
    dispatch(showModal({
      type: 'confirmation',
      message: t('Delete File'),
      subMsg: t('This file will be permanently removed from the data source. This action cannot be undone.'),
      icon: <PiTrashLight className='w-6 h-6 text-red-600' />,
      isDest: true,
      onConfirm: async () => {
        setDeletingFiles((prev) => [...prev, fileId]);
        const result = await dispatch(deleteFileFromDataSource(dataSourceUuid, fileId) as any);
        dispatch(hideModal());

        await fetchIndexFiles();
        await fetchIndexDetails();
        setDeletingFiles((prev) => prev.filter((f) => f !== fileId));

        // If the backend queued a reindex, subscribe to PubSub for completion
        if (result?.reindexing) {
          setIsReindexing(true);

          const hub = 'ai_processing';
          const group = `datasource_${dataSourceUuid}`;
          const socketService = getMultiHubSocketService();

          try {
            const connected = await socketService.connectToHub(hub);
            if (connected) {
              socketService.emit(hub, 'join', { group });

              const handleEvent = async (eventData: any) => {
                const subType = eventData?.data?.sub_type || '';
                const eventDsUuid = eventData?.data?.datasource_uuid || '';
                if (eventDsUuid !== dataSourceUuid) return;

                if (subType === 'reindex_completed' || subType === 'reindex_failed') {
                  setIsReindexing(false);
                  await fetchIndexFiles();
                  await fetchIndexDetails();
                  socketService.emit(hub, 'leave', { group });
                  socketService.off(hub, '*', handleEvent);
                }
              };

              socketService.on(hub, '*', handleEvent);

              // Safety timeout: clear after 5 minutes if no event received
              setTimeout(() => {
                setIsReindexing(false);
                socketService.emit(hub, 'leave', { group });
                socketService.off(hub, '*', handleEvent);
              }, 5 * 60 * 1000);
            }
          } catch (err) {
            console.warn('[AIStudioIndexDetailPage] PubSub connection failed, falling back', err);
            setIsReindexing(false);
          }
        }
      },
      onCancel: () => { },
      showModal: true,
    }));
  };

  const formatDate = (dateString: string | null) =>
    dateString ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(dateString)) : t('N/A');

  const renderSkeleton = () => (
    <div className="space-y-6 animate-fadePulse">
      <div className="h-8 w-1/3 bg-gray-300 dark:bg-gray-600 rounded"></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-6 w-3/4 bg-gray-300 dark:bg-gray-600 rounded"></div>
        ))}
      </div>
      <div className="h-10 w-full bg-gray-300 dark:bg-gray-600 rounded"></div>
      <div className="space-y-2">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-4 w-full bg-gray-300 dark:bg-gray-600 rounded"></div>
        ))}
      </div>
    </div>
  );

  const handleOpenFile = async (fileId: string | undefined, fileName: string) => {
    if (!fileId) return;

    const ext = (fileName.split('.').pop() || '').toLowerCase();
    const contentTypeMap: Record<string, string> = {
      pdf: 'application/pdf',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      doc: 'application/msword',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      xls: 'application/vnd.ms-excel',
      txt: 'text/plain',
      csv: 'text/csv',
    };
    const contentType = contentTypeMap[ext] || 'application/octet-stream';

    // Show reader with loading state
    dispatch(showSidePanel({
      type: 'reader',
      showSidePanel: true,
      width: '65%',
      loading: true,
      message: fileName,
      subMsg: t('Document Preview'),
      matterId: '',
      sourceType: 'document',
      citation: {
        title: fileName,
        name: fileName,
        contentType,
      },
    }));

    try {
      const result = await dispatch(downloadDataSourceFile(dataSourceUuid, fileId) as any);
      if (result?.url) {
        // Fetch as blob from the object URL
        const resp = await fetch(result.url);
        const blob = await resp.blob();

        dispatch(showSidePanel({
          type: 'reader',
          showSidePanel: true,
          width: '65%',
          loading: false,
          message: fileName,
          subMsg: t('Document Preview'),
          matterId: '',
          sourceType: 'document',
          userAccessLevel: 'view',
          citation: {
            title: fileName,
            name: fileName,
            blob,
            blobObject: blob,
            contentType: result.contentType || contentType,
          },
        }));
      }
    } catch (err) {
      console.error('Failed to open file:', err);
    }
  };


  const handleDownloadFile = async (fileId: string | undefined, fileName: string) => {
    if (!fileId) return;
    const result = await dispatch(downloadDataSourceFile(dataSourceUuid, fileId) as any);
    if (result?.url) {
      const a = document.createElement('a');
      a.href = result.url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(result.url);
    }
  };


  const handleCopyLink = async (file: { url?: string; name: string }) => {
    if (!file.url) return;
    try {
      await navigator.clipboard.writeText(file.url);
    } catch {
      window.prompt("Copy URL", file.url);
    }
  };




  const handleSharedUpdate = async (
    nextShared: Array<{ id: string; email: string; name: string; initials: string; avatarUrl: string; role: string; access?: AccessLevel }>
  ) => {

    const payload = (nextShared || []).map((u) => ({
      id: u.id,
      name: u.name,
      email: u.email,
      initials: u.initials,
      avatarUrl: u.avatarUrl,
      role: u.role,
      access: (u.access || "view") as "view" | "edit",
    }))
      .filter((x) => x.id);


    const updated = await dispatch<any>(updateDataSourceSharedAccess(dataSourceUuid, payload));

    return updated;
  };


  const handleRevokeAccess = async (id: string) => {
    await dispatch<any>(revokeSharedAccess(dataSourceUuid, id));
  }

  const handleSaveDataSource = useCallback(async (updates: { name?: string; description?: string; type?: string; keywords?: string[] }) => {
    const result = await dispatch<any>(updateDataSource(dataSourceUuid, updates));
    if (result?.status === 'success') {
      // Refresh data
      const refreshed = await dispatch<any>(getDataSourceInfo(dataSourceUuid));
      if (refreshed?.status === 'success') {
        setIndexInfo(refreshed.data);
      }
      return true;
    }
    return false;
  }, [dataSourceUuid, dispatch]);

const dataSourceBreadcrumbs = [
    { name: t('Data Sources'), href: '/data-sources', current: false },
    { name: indexInfo?.DataSourceName || t('Loading...'), href: `/data-sources/${dataSourceUuid}`, current: true },
  ];

  const dataSourceDropdownActions = [
    {
      label: t('Manage Access'),
      onClick: handleManageAccess,
      icon: <PiShareNetworkLight className="h-4 w-4" />,
      variant: 'default',
      disabled: false,
    },
    {
      label: t('Delete Data Source'),
      onClick: handleDeleteIndex,
      icon: <PiTrashLight className="h-4 w-4 text-red-900" />,
      variant: 'destructive',
      disabled: isDeleting,
    },
  ];

  const additionalHeaderElements = (
    <div data-tour-id="ds-detail-header" className='flex flex-row gap-3 items-center'>
      <ProjectBadges resourceType="datasource" resourceUuid={dataSourceUuid} />
      <PendingAccessRequests resourceType="datasource" resourceUuid={dataSourceUuid} />
      {/* Collaborator avatars */}
      {collaborators.length > 0 && (
        <div className="flex items-center">
          {collaborators.slice(0, 3).map((collab, idx) => (
            <div key={idx} className={clsx('relative rounded-full', idx > 0 && 'ml-[-0.5rem]')}>
              <CollaboratorBadge collaborator={collab} />
            </div>
          ))}
          {collaborators.length > 3 && (
            <div
              className="relative ml-[-0.5rem]"
              title={collaborators.slice(3).map(c => `${c.name} (${c.access || 'view'})`).join(', ')}
            >
              <div className="h-7 w-7 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-300 border-2 border-white dark:border-gray-900">
                +{collaborators.length - 3}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Actions dropdown */}
      <Menu as="div" className="relative inline-block text-left z-50">
        <Menu.Button as={Button} variant="outline" size="small" className="p-1">
          <PiDotsThreeVerticalLight className="h-4 w-4" />
        </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 dark:divide-gray-700 rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="px-1 py-1">
            {dataSourceDropdownActions.filter(a => a.variant !== 'destructive').map((action, index) => (
              <Menu.Item key={index}>
                {({ active }) => (
                  <button
                    className={`${active ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'} group flex w-full items-center rounded-md px-2 py-2 text-sm gap-2`}
                    onClick={action.onClick}
                    disabled={action.disabled}
                  >
                    {action.icon}
                    {t(action.label)}
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
          <div className="px-1 py-1">
            {dataSourceDropdownActions.filter(a => a.variant === 'destructive').map((action, index) => (
              <Menu.Item key={index}>
                {({ active }) => (
                  <button
                    className={`${active ? 'bg-red-50 dark:bg-red-900/30' : ''} text-red-700 dark:text-red-400 group flex w-full items-center rounded-md px-2 py-2 text-sm gap-2`}
                    onClick={action.onClick}
                    disabled={action.disabled}
                  >
                    {action.icon}
                    {t(action.label)}
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Transition>
      </Menu>
    </div>
  );

  const formatSize = (bytes?: number) => {
    if (!bytes && bytes !== 0) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let i = -1;
    let size = bytes;
    do {
      size = size / 1024;
      i++;
    } while (size >= 1024 && i < units.length - 1);
    return `${size.toFixed(1)} ${units[i]}`;
  };

  const subHeaderRight = indexInfo && (
    <div data-tour-id="ds-detail-badges" className="flex flex-wrap gap-2">
      <Badge
        children={indexInfo.visibility === 'private' ? 'Personal' : indexInfo.visibility === 'organization' ? 'Company' : 'Shared'}
        tooltip={t('Data Source Type')}
        icon={<PiDiamondsFourLight size={20} />}
        color='gray'
      />
      <Badge
        children={indexInfo.document_count || 0}
        tooltip={t('Number of Files')}
        icon={<PiFileLight size={20} />}
        color='gray'
      />
      <Badge
        children={formatDate(indexInfo.last_indexed_at)}
        tooltip={t('Last Run')}
        icon={<PiClockLight size={20} />}
        color='gray'
      />
      <Badge
        children={formatSize(indexInfo.total_size_bytes)}
        tooltip={t('Size')}
        icon={<PiDatabaseLight size={20} />}
        color='gray'
      />
    </div>
  );

  return (
  <DetailFrame
      breadcrumbs={dataSourceBreadcrumbs}
      dropdownActions={[]}
      subscriptionActive={subscriptionActive}
      additionalHeaderElements={additionalHeaderElements}
      showBackButton={true}
      subHeaderRight={subHeaderRight} 
      className="animate-fade-in-up h-full flex flex-col"
    >
      {isLoading ? (
        renderSkeleton()
      ) : (
        <>
          <div data-tour-id="ds-detail-tabs" className="py-2 border-1 border-t border-gray-200 dark:border-gray-700">
            <FilterBarWithTabs tabs={tabs} activeTab={activeTab} onTabClick={setActiveTab} noParentPadding={true} rightTabs={rightTabs} noParentHeight={true} />
          </div>
          <div className="flex-1 overflow-hidden">

            {
              activeTab === "details" && (
                <DataSourceDetailsPanel loading={false} dataSource={indexInfo} canShare={true} canEdit={true} onShareUpdate={(list) => handleSharedUpdate(list)} onRevokeAccess={handleRevokeAccess} onSave={handleSaveDataSource} t={t}/>
              )
            }

            {activeTab === 'files' && (
              <DataSourceFiles
                t={t}
                filesInIndex={filesInIndex}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                deletingFiles={deletingFiles}
                handleOpenFile={handleOpenFile}
                handleDownloadFile={handleDownloadFile}
                handleCopyLink={handleCopyLink}
                handleDeleteFile={handleDeleteFile}
                filesToAdd={filesToAdd}
                setFilesToAdd={setFilesToAdd}
                handleAddData={handleAddData}
                isFilesLoading={isFilesLoading}
                isConnecting={isConnecting}
                steps={steps}
                connected={connected}
                complete={complete}
                ok={ok}
                summary={summary}
                startedAt={startedAt}
                isFetchingFiles={isFetchingFiles}
                isReindexing={isReindexing}
              />
            )}

            {activeTab === 'test' && (
              <div data-tour-id="ds-test-chat" className="relative flex flex-col flex-1 h-full min-h-0 overflow-hidden">
                {isBetaEnv && (
                  <div className="absolute inset-0 bg-gray-200 bg-opacity-75 flex items-center justify-center z-10">
                    <Badge color="gray">{t('Coming soon')}</Badge>
                  </div>
                )}
                <div className="flex flex-col h-full min-h-0 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                  <div className="flex-1 min-h-0 p-4 overflow-y-auto space-y-4 bg-gray-50 dark:bg-gray-800">
                    {chatHistory.length === 0 && !isChatLoading ? (
                      <div className="text-center text-gray-500 dark:text-gray-400 flex-1 self-center flex items-center justify-center h-4/5">
                        {t('Start the conversation by entering a prompt below.')}
                      </div>
                    ) : (
                      chatHistory.map((msg, index) => (
                        <div
                          key={index}
                          className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`p-3 rounded-lg ${msg.role === 'user'
                              ? 'bg-gray-200 dark:bg-gray-700 dark:text-white text-black w-fit max-w-lg'
                              : 'text-black dark:text-white'
                              }`}
                          >
                            <ReactMarkdown remarkPlugins={[remarkGfm]} className="prose dark:prose-invert">
                              {msg.content}
                            </ReactMarkdown>
                          </div>
                        </div>
                      ))
                    )}
                    {isChatLoading && (
                      <div className="flex justify-start">
                        <div className="max-w-xs flex items-center gap-2 p-3 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white">
                          <div className="animate-spin rounded-full w-4 h-4 border-t-2 border-b-2 border-primary-500"></div>
                          <div className="text-xs animate-fadePulse">{t('Thinking...')}</div>
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                  <div data-tour-id="ds-test-input" className="p-4 border-t border-gray-200 dark:border-gray-600 shrink-0">
                    <div className="flex items-center space-x-2">
                      <Input
                        type="text"
                        placeholder={t('Enter your prompt...')}
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="flex-1 border-gray-300 dark:border-gray-600 focus:ring-primary-500"
                      />
                      <Button
                        variant="primary"
                        size="small"
                        onClick={handleChatSend}
                        disabled={chatInput === ''}
                        className={`${chatInput === '' && 'opacity-50 cursor-not-allowed'} relative`}
                      >
                        <PiPaperPlaneLight size={18} />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
      <FeatureTour steps={dataSourceDetailTourSteps} run={isDetailTourRunning} onCallback={handleDetailTourCallback} continuous showProgress showSkipButton disableOverlay />
      <FeatureTour steps={dataSourceDetailsPanelTourSteps} run={isDetailsPanelTourRunning} onCallback={handleDetailsPanelTourCallback} continuous showProgress showSkipButton disableOverlay />
      <FeatureTour steps={dataSourceFilesPanelTourSteps} run={isFilesPanelTourRunning} onCallback={handleFilesPanelTourCallback} continuous showProgress showSkipButton disableOverlay />
      <FeatureTour steps={dataSourceTestTourSteps} run={isTestTourRunning} onCallback={handleTestTourCallback} continuous showProgress showSkipButton disableOverlay />
    </DetailFrame>
  );
};

export default AIStudioIndexDetailPage;